import { createSlice } from '@reduxjs/toolkit';
import { listShippers } from 'src/services/ShipperService';
import { getSelectBoxOptions } from 'src/utils/CommonFunctions';

interface ICommonCarrierRequiredField {
    slug: string;
    carrier: string[];
    bol: string[];
}

interface AddressState {
    isShipperLoading: boolean;
    shippers: any[];
    consignees: any[];
    shipperOptions: any[];
    consigneeOptions: any[];
    searchShipper: string | null;
    searchConsignee: string | null;
    commonCarrierRequiredFields: ICommonCarrierRequiredField[];
}

const initialState: AddressState = {
    isShipperLoading: false,
    shippers: [],
    consignees: [],
    shipperOptions: [],
    consigneeOptions: [],
    searchShipper: null,
    searchConsignee: null,
    commonCarrierRequiredFields: [],
};

const addressSlice = createSlice({
    name: 'address',
    initialState,
    reducers: {
        setIsShipperLoading: (state, action) => {
            state.isShipperLoading = action.payload;
        },
        setShippers: (state, action) => {
            state.shippers = action.payload;
        },
        setConsignees: (state, action) => {
            state.consignees = action.payload;
        },
        setShipperOptions: (state, action) => {
            state.shipperOptions = action.payload;
        },
        setConsigneeOptions: (state, action) => {
            state.consigneeOptions = action.payload;
        },
        setSearchShipper: (state, action) => {
            state.searchShipper = action.payload;
        },
        setSearchConsignee: (state, action) => {
            state.searchConsignee = action.payload;
        },
        setCommonCarrierRequiredFields: (state, action) => {
            state.commonCarrierRequiredFields = action.payload ?? [];
        },
    },
});

export const { setIsShipperLoading, setShippers, setConsignees, setShipperOptions, setConsigneeOptions, setSearchShipper, setSearchConsignee, setCommonCarrierRequiredFields } = addressSlice.actions;

export const selectCommonCarrierRequiredFields = (state: any): ICommonCarrierRequiredField[] => state.addresses?.commonCarrierRequiredFields ?? [];

/**
 * Binds the /common/init `shipperConsignee` payload into Redux.
 *
 * Shared by the single boot call and the standalone refetch below, so both split
 * shippers/consignees and build the 5-item select options identically. Returns the split lists so
 * the caller can still resolve a default selection from them.
 */
export const applyShipperConsigneeData = (dispatch: any, list: any, commonCarrierRequiredFields?: any) => {
    const shipperOptionsValue: any[] = [];
    const consigneeOptionsValue: any[] = [];

    if (Array.isArray(list)) {
        list.forEach((result: any) => {
            if (result.type === 1) {
                shipperOptionsValue.push(result);
            } else {
                consigneeOptionsValue.push(result);
            }
        });
    } else {
        // Log for debugging
        console.warn('Unexpected shipper/consignee payload:', list);
    }

    dispatch(setShippers(shipperOptionsValue));
    dispatch(setConsignees(consigneeOptionsValue));

    const filteredShipperOptionsValue = getSelectBoxOptions(shipperOptionsValue.slice(0, 5), 'id', 'companyName');
    const filteredConsigneeOptionsValue = getSelectBoxOptions(consigneeOptionsValue.slice(0, 5), 'id', 'companyName');

    dispatch(setShipperOptions(filteredShipperOptionsValue));
    dispatch(setConsigneeOptions(filteredConsigneeOptionsValue));

    if (commonCarrierRequiredFields) {
        dispatch(setCommonCarrierRequiredFields(commonCarrierRequiredFields));
    }

    return { shippers: shipperOptionsValue, consignees: consigneeOptionsValue };
};

// Thunks

/**
 * Standalone refetch of /address-book/get-shipper-consignee.
 * NOT called on page load — app-boot data comes from /common/init. This runs only after the user
 * creates/updates a shipper or consignee, where a fresh list is genuinely required.
 */
export const fetchShippers =
    (defaultId: string | null = null, defaultType: number = 1, setSelectFormData: any = null, type: string | null = null): any =>
    async (dispatch: any) => {
        dispatch(setIsShipperLoading(true));

        try {
            const response = await listShippers();

            if (response?.data && response?.status) {
                const responseAny = response as any;
                const { shippers: shipperOptionsValue, consignees: consigneeOptionsValue } = applyShipperConsigneeData(dispatch, response.data, responseAny?.commonCarrierRequiredFields);

                if (defaultId) {
                    const newData: any = {};

                    if (defaultType === 1) {
                        newData.shipperId = shipperOptionsValue.find((shipperOption: any) => shipperOption.id === defaultId);
                    } else {
                        newData.consigneeId = consigneeOptionsValue.find((consigneeOption: any) => consigneeOption.id === defaultId);
                    }

                    if (setSelectFormData) {
                        const responseData = type === 'shipper' ? newData.shipperId : newData.consigneeId;

                        if (type === 'shipper') {
                            const transformedData = {
                                shipperId: {
                                    value: responseData.id,
                                    label: responseData.companyName,
                                },
                                shipperAddress: {
                                    fullAddress: responseData.fullAddress,
                                },
                                isDiffshipperAdditionalServiceIds: false,
                            };
                            setSelectFormData((old: any) => ({
                                ...old,
                                ...transformedData,
                            }));
                        } else {
                            const transformedData = {
                                consigneeId: {
                                    value: responseData.id,
                                    label: responseData.companyName,
                                },
                                consigneeAddress: {
                                    fullAddress: responseData.fullAddress,
                                },
                                isDiffconsigneeAdditionalServiceIds: false,
                            };
                            setSelectFormData((old: any) => ({
                                ...old,
                                ...transformedData,
                            }));
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching shippers:', error);
        } finally {
            dispatch(setIsShipperLoading(false));
        }
    };

export const searchShippers =
    (searchTerm: string): any =>
    async (dispatch: any, getState: any) => {
        const { shippers } = getState().addresses;

        if (searchTerm) {
            const filteredOptions = getSelectBoxOptions(shippers.filter((shipper: any) => shipper?.companyName?.toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 5), 'id', 'companyName');
            dispatch(setShipperOptions(filteredOptions));
        } else {
            const defaultOptions = getSelectBoxOptions(shippers.slice(0, 5), 'id', 'companyName');
            dispatch(setShipperOptions(defaultOptions));
        }
    };

export const searchConsignees =
    (searchTerm: string): any =>
    async (dispatch: any, getState: any) => {
        const { consignees } = getState().addresses;

        if (searchTerm) {
            const filteredOptions = getSelectBoxOptions(consignees.filter((consignee: any) => consignee.companyName.toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 5), 'id', 'companyName');
            dispatch(setConsigneeOptions(filteredOptions));
        } else {
            const defaultOptions = getSelectBoxOptions(consignees.slice(0, 5), 'id', 'companyName');
            dispatch(setConsigneeOptions(defaultOptions));
        }
    };

export default addressSlice.reducer;
